import { useMemo } from "react";
import useChartSelections from "components/charting/hooks/useChartSelections";
import { MetadataType } from "types/metadata";
import useChartLineVariantSeparator from "features/Charting/hooks/useChartLineVariantSeparator";
import useGetOneRepMaxesByAthleteIdsQuery from "hooks/useGetOneRepMaxesByAthleteIdsQuery";
import useOneRepMaxSourceQueryState, {
  OneRepMaxSource,
} from "components/charting/hooks/useOneRepMaxSourceQueryState/useOneRepMaxSourceQueryState";
import useGetAthleteLoadVelocityProfilesQuery from "hooks/useGetAthleteLoadVelocityProfilesQuery";
import OneRepMaxChartHelpers from "components/charting/OneRepMaxChartV2/helpers/OneRepMaxChartHelpers";
import useStatusBuilder from "hooks/useStatusBuilder";
import type { LineData } from "components/charting/types/Chart.types";
import type { Status } from "types/Status";

interface UseOneRepMaxChartDataApiProps {}

function useOneRepMaxChartDataApi(props?: UseOneRepMaxChartDataApiProps): { data: LineData[]; status: Status } {
  const selections = useChartSelections();
  const [oneRepMaxSource] = useOneRepMaxSourceQueryState();

  const measure = selections.measures[0];
  const source = selections.sources[0];
  const startDate = selections.startDate;
  const endDate = selections.endDate;

  const range = useMemo(
    () => ({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }),
    [startDate, endDate]
  );

  const exerciseId = useMemo(() => {
    if (measure.type !== MetadataType.EXERCISE) return null;
    return measure.metadata.id;
  }, [measure]);

  const variants = useMemo(() => {
    if (measure.type !== MetadataType.EXERCISE) return undefined;
    return measure.metadata.variants;
  }, [measure]);

  const athleteIds = useMemo(() => {
    if (source && source.id) return [source.id];
    return [];
  }, [source]);

  const { data: oneRepMaxesData, status: oneRepMaxesStatus } = useGetOneRepMaxesByAthleteIdsQuery(
    exerciseId,
    athleteIds,
    {
      mostRecent: false,
    },
    {
      enabled: oneRepMaxSource === OneRepMaxSource.Manual,
    }
  );
  const { data: velocityData, status: velocityStatus } = useGetAthleteLoadVelocityProfilesQuery(
    { athleteId: athleteIds[0], exerciseId, params: range, variants },
    { enabled: oneRepMaxSource === OneRepMaxSource.Velocity }
  );

  const status = useStatusBuilder(
    useMemo(() => [oneRepMaxesStatus, velocityStatus], [oneRepMaxesStatus, velocityStatus])
  );

  const oneRepMaxChartData = useMemo(() => {
    if (oneRepMaxSource === OneRepMaxSource.Velocity) {
      const athleteId = athleteIds[0];
      if (!athleteId || !exerciseId) return [];
      return OneRepMaxChartHelpers.toVelocityChartData(velocityData, athleteId, exerciseId);
    }
    return OneRepMaxChartHelpers.toOneRepMaxChartData(oneRepMaxesData, range.startDate, range.endDate);
  }, [oneRepMaxSource, velocityData, oneRepMaxesData, athleteIds, exerciseId, range]);

  const entityIds = useMemo<string[]>(() => {
    if (measure.type !== MetadataType.EXERCISE) return [];
    return [measure.metadata.id];
  }, [measure]);

  const data = useChartLineVariantSeparator(oneRepMaxChartData, entityIds);

  return useMemo(() => ({ data, status }), [data, status]);
}

export default useOneRepMaxChartDataApi;
